import { track } from '@/lib/measurement';
import { STORAGE_KEYS, storage } from '@/lib/storage/keys';
import { ApiError, authHeaders } from './http';

export const TIERS = ['subscriber', 'shadow_work', 'shadow_oracle', 'premium'] as const;
export type Tier = (typeof TIERS)[number];

export interface User {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly tier: Tier;
}

export interface Session {
  readonly token: string;
  readonly user: User;
}

function errorMessage(body: Record<string, unknown>, fallback: string): string {
  if (typeof body.error === 'string' && body.error !== '') return body.error;
  if (Array.isArray(body.errors) && body.errors.length > 0) return body.errors.join('. ');
  return fallback;
}

/** Reads a login/register response: `{success:true, token, user}` or throws with the server's own message. */
async function readSession(response: Response, fallback: string): Promise<Session> {
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  const body = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  if (body.success !== true || typeof body.token !== 'string' || typeof body.user !== 'object' || body.user === null) {
    throw new ApiError(errorMessage(body, fallback), response.status, parsed);
  }
  return { token: body.token, user: body.user as User };
}

function postJson(path: string, input: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    cache: 'no-store',
  });
}

/** POST /api/auth/register.php. An OK response emits `sign_up {method:'account'}` (as production's network hook did). */
export async function register(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}): Promise<Session> {
  const response = await postJson('/api/auth/register.php', input);
  if (response.ok) track('sign_up', { method: 'account' });
  return readSession(response, 'Registration failed');
}

/** POST /api/auth/login.php. */
export async function login(input: { email: string; password: string }): Promise<Session> {
  return readSession(await postJson('/api/auth/login.php', input), 'Login failed');
}

/** GET /api/auth/me.php with the Bearer token; null when the server does not confirm the session. */
export async function me(token: string): Promise<User | null> {
  const response = await fetch('/api/auth/me.php', { headers: authHeaders(token), cache: 'no-store' });
  if (!response.ok) return null;
  const body = (await response.json()) as { success?: boolean; user?: User };
  return body.success === true && body.user !== undefined ? body.user : null;
}

export async function logout(token: string): Promise<void> {
  await fetch('/api/auth/logout.php', { method: 'POST', headers: authHeaders(token) });
}

/**
 * The stored session token, after applying the boot rule every surface shares: a token whose
 * `mutatio618_token_expires` (ms epoch, written by /claim/) has passed is removed together with
 * its expiry, so a dead session reads as signed out (omega-gauntlet.js boot rule).
 */
export function readStoredToken(now: number = Date.now()): string | null {
  const expires = Number(storage.get(STORAGE_KEYS.tokenExpires));
  if (expires && now > expires) {
    storage.remove(STORAGE_KEYS.accessToken);
    storage.remove(STORAGE_KEYS.tokenExpires);
  }
  return storage.get(STORAGE_KEYS.accessToken);
}

/** A session obtained here has no client-side expiry; a stale expiry left by an earlier session is dropped. */
export function storeSession(token: string): void {
  storage.set(STORAGE_KEYS.accessToken, token);
  storage.remove(STORAGE_KEYS.tokenExpires);
}

export function clearStoredSession(): void {
  storage.remove(STORAGE_KEYS.accessToken);
  storage.remove(STORAGE_KEYS.tokenExpires);
}
